import { useState, useMemo, useEffect, useRef } from "react";
import { RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import InputField from "./InputField";
import ResultPanel from "./ResultPanel";
import ScenarioCompare from "./ScenarioCompare";
import SavedScenarios from "./SavedScenarios";
import PlusUpsell from "../common/PlusUpsell";
import ProductRecommendation from "../product/ProductRecommendation";
import Disclaimer from "../common/Disclaimer";
import { analytics } from "../../lib/analytics/ga4";

function defaultsFrom(inputs) {
  const d = {};
  inputs.forEach((i) => { d[i.id] = i.default; });
  return d;
}

const NAMES = ["Scenario A", "Scenario B"];
const keyFor = (slug) => `mfg:scn:${slug}`;

function loadStored(slug) {
  try {
    const raw = localStorage.getItem(keyFor(slug));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export default function CalculatorEngine({ calc }) {
  const [values, setValues] = useState(() => defaultsFrom(calc.inputs));
  const [scenarios, setScenarios] = useState(() => loadStored(calc.slug));
  const started = useRef(false);
  const completedTimer = useRef(null);

  useEffect(() => {
    setValues(defaultsFrom(calc.inputs));
    setScenarios(loadStored(calc.slug));
    started.current = false;
    analytics.calculatorView(calc.slug);
  }, [calc.slug]); // eslint-disable-line

  const result = useMemo(() => {
    try { return calc.compute(values); } catch (e) { return null; }
  }, [values, calc]);

  const persist = (next) => {
    setScenarios(next);
    try { localStorage.setItem(keyFor(calc.slug), JSON.stringify(next)); } catch { /* ignore */ }
  };

  const onChange = (id, v) => {
    if (!started.current) { analytics.calculatorStarted(calc.slug); started.current = true; }
    setValues((prev) => ({ ...prev, [id]: v }));
    clearTimeout(completedTimer.current);
    completedTimer.current = setTimeout(() => analytics.calculatorCompleted(calc.slug), 1200);
  };

  const reset = () => {
    setValues(defaultsFrom(calc.inputs));
    analytics.calculatorReset(calc.slug);
  };

  const saveScenario = () => {
    if (!result) return;
    const snap = { id: Date.now(), values: { ...values }, primary: result.primary, secondary: result.secondary || [] };
    const next = [...scenarios, snap].slice(-2).map((s, i) => ({ ...s, name: NAMES[i] }));
    persist(next);
    analytics.calculatorCompleted(calc.slug);
    toast.success(next.length === 2 ? "Saved — compare A vs B below" : "Scenario saved. Change inputs and save another to compare.");
  };

  const loadScenario = (vals) => {
    setValues({ ...vals });
    started.current = true;
    toast.success("Scenario loaded into the calculator");
  };

  const removeScenario = (id) => {
    const next = scenarios.filter((s) => s.id !== id).map((s, i) => ({ ...s, name: NAMES[i] }));
    persist(next);
  };
  const clearScenarios = () => persist([]);

  const Controls = (
    <div className="flex items-center gap-3">
      <button onClick={saveScenario} data-testid="save-scenario" className="inline-flex items-center gap-1 text-xs font-medium text-brand transition-colors hover:text-action">
        <Save className="h-3.5 w-3.5" /> Save scenario
      </button>
      <button onClick={reset} data-testid="reset-calculator" className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-brand">
        <RotateCcw className="h-3.5 w-3.5" /> Reset
      </button>
    </div>
  );

  const Extras = (
    <>
      <ScenarioCompare scenarios={scenarios} onLoad={loadScenario} onRemove={removeScenario} onClear={clearScenarios} />
      <SavedScenarios calc={calc} values={values} onLoad={loadScenario} />
      <PlusUpsell />
      {calc.recommendedProduct && <ProductRecommendation productId={calc.recommendedProduct} calculatorName={calc.name} />}
    </>
  );

  const hasList = calc.inputs.some((i) => i.type === "list");

  if (hasList) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-foreground">Your details</h2>
            {Controls}
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {calc.inputs.map((input) => (
              <div key={input.id} className={input.type === "list" ? "md:col-span-2" : ""}>
                <InputField input={input} value={values[input.id]} onChange={(v) => onChange(input.id, v)} />
              </div>
            ))}
          </div>
          <div className="mt-6"><Disclaimer variant="inline" /></div>
        </div>
        <ResultPanel result={result} calc={calc} values={values} />
        {Extras}
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-1">
        <div className="sticky top-20 rounded-2xl border border-border bg-card p-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-foreground">Your details</h2>
            {Controls}
          </div>
          <div className="space-y-5">
            {calc.inputs.map((input) => (
              <InputField key={input.id} input={input} value={values[input.id]} onChange={(v) => onChange(input.id, v)} />
            ))}
          </div>
          <div className="mt-6"><Disclaimer variant="inline" /></div>
        </div>
      </div>

      <div className="space-y-6 lg:col-span-2">
        <ResultPanel result={result} calc={calc} values={values} />
        {Extras}
      </div>
    </div>
  );
}
